import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { getToken } from "@/lib/auth.jsx";
import { useAuth } from "@/lib/auth.jsx";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Send, UserCheck, FileText, Image as ImageIcon, MessageSquare, Activity, Download, Loader2, Archive, ArchiveRestore, Calendar, Video, Upload, Trash2, Paperclip, X, Sparkles, Pencil, Check, RefreshCw, Database, ExternalLink } from "lucide-react";

const firstName = (full) => (full || "").trim().split(/\s+/)[0] || "";

const ROLE_LABELS = {
    admin: "administrator",
    kundeservice: "kundeservice",
    medarbejder: "medarbejder",
    leder: "leder",
};
const roleLabel = (role) => ROLE_LABELS[role] || "kundeservice";

// Deterministic agent bubble color from sender_id (or name as fallback)
const AGENT_COLORS = [
    "#4A4641", // warm dark
    "#2D5F4E", // forest green
    "#5B4B8A", // muted purple
    "#8B4513", // sienna
    "#1E4D5C", // teal
    "#7A4E2E", // tobacco
    "#4F5D75", // slate blue
    "#6B3E2E", // chestnut
];

const agentColor = (key) => {
    const s = String(key || "");
    let hash = 0;
    for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
    return AGENT_COLORS[Math.abs(hash) % AGENT_COLORS.length];
};

export default function CaseDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const isMedarbejder = user?.role === "medarbejder";
    const [complaint, setComplaint] = useState(null);
    const [meta, setMeta] = useState(null);
    const [users, setUsers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [leaders, setLeaders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newNote, setNewNote] = useState("");
    const [newMessage, setNewMessage] = useState("");
    const [sendingMessage, setSendingMessage] = useState(false);
    const [includeBooking, setIncludeBooking] = useState(false);
    const [includeBooking4w, setIncludeBooking4w] = useState(false);
    const [includeBooking6w, setIncludeBooking6w] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [uploadingReceipt, setUploadingReceipt] = useState(false);
    const [chatAttachments, setChatAttachments] = useState([]); // [{id, filename, ext, content_type, localUrl}]
    const [chatUploading, setChatUploading] = useState(false);
    const [aiDrafting, setAiDrafting] = useState(false);
    const [aiDraft, setAiDraft] = useState(null); // {draft, suggested_booking, reasoning}
    const filesInputRef = useRef(null);
    const receiptInputRef = useRef(null);
    const chatAttachRef = useRef(null);

    const uploadChatAttachments = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setChatUploading(true);
        try {
            for (const file of files) {
                const ext = file.name.split(".").pop()?.toLowerCase() || "";
                if (!["jpg", "jpeg", "png", "heic", "heif", "webp", "pdf"].includes(ext)) {
                    toast.error(`${file.name}: filtype ikke tilladt`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", file);
                try {
                    const { data } = await api.post("/agent/upload", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    setChatAttachments((prev) => [
                        ...prev,
                        {
                            id: data.id,
                            filename: data.filename,
                            ext: data.ext,
                            content_type: data.content_type,
                            localUrl: URL.createObjectURL(file),
                        },
                    ]);
                } catch (e) {
                    toast.error(`${file.name}: ${e.response?.data?.detail || "upload fejlede"}`);
                }
            }
        } finally {
            setChatUploading(false);
        }
    };

    const removeChatAttachment = (fid) => {
        setChatAttachments((prev) => {
            const target = prev.find((a) => a.id === fid);
            if (target?.localUrl) URL.revokeObjectURL(target.localUrl);
            return prev.filter((a) => a.id !== fid);
        });
    };

    const generateAIDraft = async () => {
        setAiDrafting(true);
        setAiDraft(null);
        try {
            const { data } = await api.post(`/complaints/${id}/ai-draft`);
            setAiDraft(data);
            toast.success("AI-forslag klar");
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI kunne ikke generere et forslag");
        } finally {
            setAiDrafting(false);
        }
    };

    const applyAIDraft = () => {
        if (!aiDraft) return;
        setNewMessage(aiDraft.draft);
        // Auto-tick booking checkbox based on suggestion; keep exclusive selection
        const b = aiDraft.suggested_booking;
        setIncludeBooking(b === "asap");
        setIncludeBooking4w(b === "4w");
        setIncludeBooking6w(b === "6w");
        // Auto-add AI-attached files to chat attachments (dedupe by id)
        const incoming = (aiDraft.attachments || []).map((f) => ({
            id: f.id,
            filename: f.filename,
            content_type: f.content_type,
            size: f.size,
            ext: (f.filename || "").split(".").pop()?.toLowerCase() || "",
        }));
        if (incoming.length) {
            setChatAttachments((prev) => {
                const existing = new Set(prev.map((a) => a.id));
                return [...prev, ...incoming.filter((a) => !existing.has(a.id))];
            });
        }
        setAiDraft(null);
        toast.success(
            incoming.length
                ? `Draft indsat med ${incoming.length} vedhæftning(er) — gennemgå og send`
                : "Draft indsat — gennemgå og send"
        );
    };

    const dismissAIDraft = () => setAiDraft(null);

    const attachFiles = async (fileList, isReceipt = false) => {
        if (!fileList || fileList.length === 0) return;
        const setBusy = isReceipt ? setUploadingReceipt : setUploadingFiles;
        setBusy(true);
        try {
            const uploaded = [];
            for (const file of Array.from(fileList)) {
                const fd = new FormData();
                fd.append("file", file);
                try {
                    const { data } = await api.post("/agent/upload", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    uploaded.push(data.id);
                } catch (e) {
                    toast.error(`${file.name}: ${e.response?.data?.detail || "upload fejlede"}`);
                }
            }
            if (uploaded.length === 0) return;
            const payload = isReceipt
                ? { receipt_file_id: uploaded[0] }
                : { file_ids: uploaded };
            await api.post(`/complaints/${id}/attach-files`, payload);
            toast.success(isReceipt ? "Kvittering vedhæftet" : `${uploaded.length} fil(er) vedhæftet`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke vedhæfte");
        } finally {
            setBusy(false);
        }
    };

    const deleteReceipt = async () => {
        if (!confirm("Slet kvitteringen fra denne sag?")) return;
        try {
            await api.delete(`/complaints/${id}/receipt`);
            toast.success("Kvittering slettet");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke slette kvittering");
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const promises = [
                api.get(`/complaints/${id}`),
                api.get("/meta/internal"),
                api.get("/suppliers"),
            ];
            if (!isMedarbejder) {
                promises.push(api.get("/users"));
                promises.push(api.get("/leaders"));
            }
            const results = await Promise.all(promises);
            setComplaint(results[0].data);
            setMeta(results[1].data);
            setSuppliers(results[2].data);
            setUsers(isMedarbejder ? [] : results[3].data);
            setLeaders(isMedarbejder ? [] : results[4].data);
        } catch (e) {
            toast.error("Kunne ikke hente sag");
        } finally {
            setLoading(false);
        }
    }, [id, isMedarbejder]);

    useEffect(() => { load(); }, [load]);

    const changeStatus = async (newStatus, notifyCustomer = false, note = "") => {
        try {
            const { data } = await api.patch(`/complaints/${id}/status`, { status: newStatus, notify_customer: notifyCustomer, note });
            setComplaint(data);
            toast.success("Status opdateret");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke opdatere status");
        }
    };

    const assign = async (payload) => {
        try {
            const { data } = await api.patch(`/complaints/${id}/assign`, payload);
            setComplaint(data);
            toast.success("Sag tildelt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke tildele");
        }
    };

    const addNote = async () => {
        if (!newNote.trim()) return;
        try {
            await api.post(`/complaints/${id}/notes`, { text: newNote });
            setNewNote("");
            await load();
            toast.success("Note tilføjet");
        } catch (e) {
            toast.error("Kunne ikke tilføje note");
        }
    };

    const sendMessage = async () => {
        const trimmed = newMessage.trim();
        if (!trimmed && chatAttachments.length === 0) return;
        setSendingMessage(true);
        try {
            await api.post(`/complaints/${id}/messages`, {
                text: trimmed,
                include_booking: includeBooking,
                include_booking_4w: includeBooking4w,
                include_booking_6w: includeBooking6w,
                attachment_ids: chatAttachments.map((a) => a.id),
            });
            setNewMessage("");
            setIncludeBooking(false);
            setIncludeBooking4w(false);
            setIncludeBooking6w(false);
            setChatAttachments([]);
            await load();
            toast.success("Beskeden er sendt til kunden");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke sende besked");
        } finally {
            setSendingMessage(false);
        }
    };

    const markHandled = async () => {
        try {
            await api.post(`/complaints/${id}/mark-handled`);
            await load();
            toast.success("Markeret som håndteret");
        } catch (e) {
            toast.error("Kunne ikke markere som håndteret");
        }
    };

    const markInternalHandled = async () => {
        try {
            await api.post(`/complaints/${id}/mark-internal-handled`);
            await load();
            toast.success("Internt svar markeret som håndteret");
        } catch (e) {
            toast.error("Kunne ikke markere som håndteret");
        }
    };

    const toggleArchive = async () => {
        const isArchived = !!complaint.archived;
        if (!isArchived && !confirm("Vil du arkivere denne sag? Den flyttes til arkivet og fjernes fra den aktive oversigt.")) return;
        try {
            await api.post(`/complaints/${id}/${isArchived ? "unarchive" : "archive"}`);
            await load();
            toast.success(isArchived ? "Sag genåbnet fra arkiv" : "Sag arkiveret");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke ændre arkivstatus");
        }
    };

    if (loading || !complaint) {
        return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#78736E]" /></div>;
    }

    const catLabel = meta?.problem_categories?.find((c) => c.id === complaint.problem_category)?.label || complaint.problem_category;
    const token = getToken();
    const fileUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download?auth=${token}`;

    return (
        <div className="space-y-6" data-testid="case-detail-page">
            {complaint.awaiting_agent_reply && (
                <div className="flex items-center justify-between gap-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl p-4 flex-wrap" data-testid="awaiting-reply-banner">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#993C3C] text-white flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-heading text-sm font-semibold text-[#92400E]">Kunden afventer svar fra kundeservice</div>
                            <div className="text-xs text-[#92400E]/80">Send et svar via samtale-tråden eller markér som håndteret hvis sagen er klaret pr. telefon.</div>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={markHandled}
                        className="border-[#92400E]/30 bg-white text-[#92400E] hover:bg-[#FEF3C7]"
                        data-testid="mark-handled-button"
                    >
                        Markér som håndteret
                    </Button>
                </div>
            )}
            {complaint.awaiting_internal_reply && (
                <div className="flex items-center justify-between gap-4 bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl p-4 flex-wrap" data-testid="awaiting-internal-banner">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#6B5B95] text-white flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-heading text-sm font-semibold text-[#3730A3]">Leverandør/medarbejder har svaret</div>
                            <div className="text-xs text-[#3730A3]/80">Læs det interne svar nedenfor og fortsæt korrespondancen i Intern korrespondance-sektionen.</div>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={markInternalHandled}
                        className="border-[#3730A3]/30 bg-white text-[#3730A3] hover:bg-[#EEF2FF]"
                        data-testid="mark-internal-handled-button"
                    >
                        Håndteret
                    </Button>
                </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/dashboard">
                        <Button variant="ghost" size="sm" className="text-[#5C5854]" data-testid="back-to-dashboard">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Tilbage
                        </Button>
                    </Link>
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="font-heading text-2xl font-medium text-[#2C2A29]" data-testid="case-number-heading">
                                {complaint.case_number}
                            </h1>
                            <StatusBadge status={complaint.status} />
                            {complaint.archived && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F0EBE1] text-[#78736E] text-xs font-semibold uppercase tracking-wider" data-testid="archived-badge">
                                    <Archive className="w-3 h-3" />
                                    Arkiveret
                                </span>
                            )}
                        </div>
                        <div className="text-sm text-[#78736E] mt-1">
                            Oprettet {(complaint.created_at || "").slice(0, 16).replace("T", " ")}
                            {complaint.archived && complaint.archived_at && (
                                <span className="ml-2">· Arkiveret {(complaint.archived_at || "").slice(0, 10)}</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={toggleArchive}
                        data-testid="toggle-archive-button"
                        className="border-[#E5E1DA] text-[#5C5854] hover:bg-[#F0EBE1]"
                    >
                        {complaint.archived ? (
                            <><ArchiveRestore className="w-4 h-4 mr-2" />Genåbn fra arkiv</>
                        ) : (
                            <><Archive className="w-4 h-4 mr-2" />Arkivér sag</>
                        )}
                    </Button>
                    <ForwardDialog complaint={complaint} suppliers={suppliers} users={users} leaders={leaders} onSent={load} restrictToSuppliers={isMedarbejder} />
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Left: main content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Customer */}
                    <Card title="Kundeoplysninger">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <Info k="Navn" v={complaint.customer_name} />
                            <EditableField
                                label="Email"
                                value={complaint.customer_email || ""}
                                editable={!isMedarbejder}
                                inputType="email"
                                testidPrefix="customer-email"
                                onSave={async (val) => {
                                    const { data } = await api.patch(`/complaints/${complaint.id}/customer-contact`, { customer_email: val });
                                    return data;
                                }}
                                onUpdated={setComplaint}
                            />
                            <EditableField
                                label="Telefon"
                                value={complaint.customer_phone || ""}
                                editable={!isMedarbejder}
                                inputType="tel"
                                testidPrefix="customer-phone"
                                onSave={async (val) => {
                                    const { data } = await api.patch(`/complaints/${complaint.id}/customer-contact`, { customer_phone: val });
                                    return data;
                                }}
                                onUpdated={setComplaint}
                            />
                            <EditableField
                                label="Ordrenummer"
                                value={complaint.order_number || ""}
                                editable={!isMedarbejder}
                                testidPrefix="order-number"
                                onSave={async (val) => {
                                    const { data } = await api.patch(`/complaints/${complaint.id}/order-number`, { order_number: val });
                                    return data;
                                }}
                                onUpdated={setComplaint}
                            />
                            <Info k="Adresse" v={`${complaint.customer_address}, ${complaint.customer_postal} ${complaint.customer_city}`} colSpan />
                        </div>
                        <CRMPanel complaint={complaint} onRefreshed={(fresh) => setComplaint(fresh)} />
                    </Card>

                    {/* Products */}
                    <Card title="Produkt(er)">
                        <div className="space-y-2">
                            {complaint.products.map((p, i) => (
                                <div key={i} className="flex items-center gap-3 py-2 border-b border-[#F0EBE1] last:border-0">
                                    <div className="flex-1 text-sm">
                                        <div className="font-medium text-[#2C2A29]">{p.product_type}</div>
                                        <div className="text-xs text-[#78736E] mt-0.5">
                                            {p.room && `${p.room} · `}
                                            {p.color_model && `${p.color_model} · `}
                                            {p.quantity} stk.
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Problem */}
                    <Card title="Problem">
                        <div className="space-y-4">
                            <Info k="Kategori" v={catLabel} />
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-1.5">Beskrivelse</div>
                                <div className="text-sm text-[#2C2A29] whitespace-pre-wrap leading-relaxed">{complaint.problem_description}</div>
                            </div>
                            {complaint.wished_solution && <Info k="Ønsket løsning" v={complaint.wished_solution} />}
                        </div>
                    </Card>

                    {/* Files */}
                    <Card title="Vedhæftninger">
                        <Tabs defaultValue="images" className="w-full">
                            <TabsList className="bg-[#F4F2EF]">
                                <TabsTrigger value="images" data-testid="tab-images">
                                    <ImageIcon className="w-4 h-4 mr-1.5" /> Billeder ({complaint.file_ids?.length || 0})
                                </TabsTrigger>
                                <TabsTrigger value="receipt" data-testid="tab-receipt">
                                    <FileText className="w-4 h-4 mr-1.5" /> Kvittering
                                </TabsTrigger>
                            </TabsList>
                            <TabsContent value="images" className="mt-4">
                                <input
                                    ref={filesInputRef}
                                    type="file"
                                    accept="image/*,video/*,.heic,.heif,application/pdf"
                                    multiple
                                    className="hidden"
                                    data-testid="agent-attach-files-input"
                                    onChange={(e) => { attachFiles(e.target.files, false); e.target.value = ""; }}
                                />
                                <div className="flex justify-end mb-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => filesInputRef.current?.click()}
                                        disabled={uploadingFiles}
                                        data-testid="agent-attach-files-button"
                                        className="border-[#D2C9B9] hover:bg-[#F0EBE1]"
                                    >
                                        {uploadingFiles ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                                        Tilføj billeder / videoer
                                    </Button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {complaint.file_ids?.map((fid) => {
                                        const meta = (complaint.files || []).find((f) => f.id === fid) || {};
                                        const isVideo = (meta.content_type || "").startsWith("video/") || ["mp4","mov","m4v","webm","3gp","avi","mkv","qt"].includes(meta.ext);
                                        const isPdf = meta.ext === "pdf" || (meta.content_type || "").includes("pdf");
                                        return (
                                            <a
                                                key={fid}
                                                href={fileUrl(fid)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="aspect-square rounded-lg overflow-hidden border border-[#E5E1DA] bg-[#F4F2EF] hover:-translate-y-px transition-transform flex items-center justify-center"
                                                data-testid={`attachment-${fid}`}
                                            >
                                                {isVideo ? (
                                                    <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center bg-[#2C2A29]">
                                                        <Video className="w-7 h-7 text-white mb-1" />
                                                        <div className="text-[10px] text-white/80 truncate w-full">{meta.filename || "Video"}</div>
                                                    </div>
                                                ) : isPdf ? (
                                                    <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                                                        <FileText className="w-7 h-7 text-[#78736E] mb-1" />
                                                        <div className="text-[10px] text-[#78736E] truncate w-full">{meta.filename || "PDF"}</div>
                                                    </div>
                                                ) : (
                                                    <img src={fileUrl(fid)} alt="" className="w-full h-full object-cover" />
                                                )}
                                            </a>
                                        );
                                    })}
                                </div>
                            </TabsContent>
                            <TabsContent value="receipt" className="mt-4">
                                <input
                                    ref={receiptInputRef}
                                    type="file"
                                    accept="image/*,.heic,.heif,application/pdf"
                                    className="hidden"
                                    data-testid="agent-attach-receipt-input"
                                    onChange={(e) => { attachFiles(e.target.files, true); e.target.value = ""; }}
                                />
                                <div className="flex justify-end gap-2 mb-3">
                                    {complaint.receipt_file_id && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={deleteReceipt}
                                            data-testid="agent-delete-receipt-button"
                                            className="border-[#E5C2C2] text-[#993C3C] hover:bg-[#FBEAEA]"
                                        >
                                            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                            Slet kvittering
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => receiptInputRef.current?.click()}
                                        disabled={uploadingReceipt}
                                        data-testid="agent-attach-receipt-button"
                                        className="border-[#D2C9B9] hover:bg-[#F0EBE1]"
                                    >
                                        {uploadingReceipt ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                                        Upload fil
                                    </Button>
                                </div>
                                {complaint.receipt_file_id ? (
                                    <a
                                        href={fileUrl(complaint.receipt_file_id)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-3 p-4 rounded-lg border border-[#E5E1DA] hover:bg-[#F4F2EF]"
                                        data-testid="receipt-attachment"
                                    >
                                        <FileText className="w-5 h-5 text-[#4A4641]" />
                                        <div className="flex-1 text-sm font-medium">Åbn kvittering / faktura</div>
                                        <Download className="w-4 h-4 text-[#78736E]" />
                                    </a>
                                ) : (
                                    <div className="text-sm text-[#78736E] p-4 text-center">Ingen kvittering vedhæftet</div>
                                )}
                            </TabsContent>
                        </Tabs>
                    </Card>

                    {/* Customer messages */}
                    <Card title="Samtale med kunde">
                        <div className="flex items-center justify-between -mt-2 mb-3 gap-2 flex-wrap">
                            <div className="text-xs text-[#78736E]">Synlig for kunden via deres unikke link</div>
                            {complaint.access_token && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        const url = `${window.location.origin}/spor/${complaint.access_token}`;
                                        navigator.clipboard?.writeText(url);
                                        toast.success("Kundens link kopieret");
                                    }}
                                    className="text-xs h-7 text-[#78736E] hover:bg-[#F0EBE1]"
                                    data-testid="copy-customer-link"
                                >
                                    Kopiér kundens link
                                </Button>
                            )}
                        </div>
                        <div className="bg-[#FDF7E9] border border-[#F0E5C5] rounded-lg p-4 mb-4 max-h-96 overflow-y-auto" data-testid="customer-thread">
                            {(!complaint.messages || complaint.messages.length === 0) && (
                                <div className="text-sm text-[#78736E] text-center py-8">Ingen beskeder endnu</div>
                            )}
                            {complaint.messages?.map((m) => {
                                const bubbleColor = m.sender_type === "agent" ? agentColor(m.sender_id || m.sender_name) : null;
                                return (
                                <div
                                    key={m.id}
                                    className={`flex mb-3 ${m.sender_type === "agent" ? "justify-end" : "justify-start"}`}
                                    data-testid={`agent-message-${m.id}`}
                                >
                                    <div
                                        className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                                            m.sender_type === "agent"
                                                ? "text-white rounded-br-sm"
                                                : "bg-white border border-[#E5E1DA] text-[#2C2A29] rounded-bl-sm"
                                        }`}
                                        style={bubbleColor ? { backgroundColor: bubbleColor } : undefined}
                                    >
                                        <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${m.sender_type === "agent" ? "text-white/60" : "text-[#78736E]"}`}>
                                            {m.sender_type === "agent" ? `${firstName(m.sender_name)} (${roleLabel(m.sender_role)})` : `${firstName(m.sender_name)} (kunde)`}
                                        </div>
                                        <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
                                        {m.attachment_ids && m.attachment_ids.length > 0 && (
                                            <div className={`mt-2 grid gap-2 ${m.attachment_ids.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                                                {m.attachment_ids.map((fid) => {
                                                    const meta = (complaint.files || []).find((f) => f.id === fid) || {};
                                                    const isImage = (meta.content_type || "").startsWith("image/") || ["jpg","jpeg","png","heic","heif","webp"].includes(meta.ext);
                                                    const isPdf = meta.ext === "pdf" || (meta.content_type || "").includes("pdf");
                                                    return (
                                                        <a
                                                            key={fid}
                                                            href={fileUrl(fid)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className={`block rounded-lg overflow-hidden border ${m.sender_type === "agent" ? "border-white/20" : "border-[#E5E1DA]"} bg-black/5 hover:opacity-90 transition-opacity`}
                                                            data-testid={`agent-message-attachment-${fid}`}
                                                        >
                                                            {isImage ? (
                                                                <img src={fileUrl(fid)} alt={meta.filename || "vedhæftning"} className="w-full max-h-48 object-cover" />
                                                            ) : isPdf ? (
                                                                <div className={`flex items-center gap-2 p-2.5 text-xs ${m.sender_type === "agent" ? "text-white/90" : "text-[#2C2A29]"}`}>
                                                                    <FileText className="w-4 h-4 shrink-0" />
                                                                    <span className="truncate">{meta.filename || "PDF"}</span>
                                                                </div>
                                                            ) : (
                                                                <div className={`flex items-center gap-2 p-2.5 text-xs ${m.sender_type === "agent" ? "text-white/90" : "text-[#2C2A29]"}`}>
                                                                    <Paperclip className="w-4 h-4 shrink-0" />
                                                                    <span className="truncate">{meta.filename || "Vedhæftning"}</span>
                                                                </div>
                                                            )}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {m.cta && m.cta.url && (
                                            <a
                                                href={m.cta.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                                    m.sender_type === "agent"
                                                        ? "bg-white/15 hover:bg-white/25 text-white"
                                                        : "bg-[#993C3C] hover:bg-[#7E2F2F] text-white"
                                                }`}
                                                data-testid={`message-cta-${m.id}`}
                                            >
                                                <Calendar className="w-3 h-3" />
                                                {m.cta.label || "Book en tid"}
                                            </a>
                                        )}
                                        <div className={`text-[10px] mt-1 ${m.sender_type === "agent" ? "text-white/50" : "text-[#78736E]"}`}>
                                            {(m.created_at || "").slice(0, 16).replace("T", " ")}
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                        <div className="flex flex-col gap-2">
                            {aiDraft && (
                                <div className="border-2 border-dashed border-[#4A4641]/40 bg-[#F0EBE1] rounded-lg p-4" data-testid="ai-draft-card">
                                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#4A4641]">
                                            <Sparkles className="w-3.5 h-3.5" />
                                            AI-forslag til svar
                                            {aiDraft.suggested_booking && (
                                                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[#D2C9B9] text-[10px] normal-case tracking-normal text-[#5C5854]">
                                                    <Calendar className="w-3 h-3" />
                                                    Foreslår booking: {aiDraft.suggested_booking === "asap" ? "hurtigst muligt" : aiDraft.suggested_booking === "4w" ? "om 4 uger" : "om 6 uger"}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={dismissAIDraft}
                                                className="h-7 text-xs text-[#78736E] hover:bg-white"
                                                data-testid="ai-draft-dismiss"
                                            >
                                                Kassér
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={applyAIDraft}
                                                className="h-7 text-xs bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                                                data-testid="ai-draft-apply"
                                            >
                                                Godkend & rediger
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="text-sm text-[#2C2A29] whitespace-pre-wrap leading-relaxed bg-white rounded p-3 border border-[#E5E1DA]" data-testid="ai-draft-text">
                                        {aiDraft.draft}
                                    </div>
                                    {aiDraft.attachments && aiDraft.attachments.length > 0 && (
                                        <div className="mt-2" data-testid="ai-draft-attachments">
                                            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#4A4641] mb-1.5">
                                                AI vedhæfter {aiDraft.attachments.length} fil(er) fra instruks{aiDraft.attachments.length > 1 ? "er" : ""}
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {aiDraft.attachments.map((f) => (
                                                    <div
                                                        key={f.id}
                                                        className="flex items-center gap-1.5 bg-white border border-[#D2C9B9] rounded px-2 py-1 text-[11px] text-[#2C2A29]"
                                                        data-testid={`ai-draft-attachment-${f.id}`}
                                                        title={f.filename}
                                                    >
                                                        <Paperclip className="w-3 h-3 text-[#78736E]" />
                                                        <span className="max-w-[180px] truncate">{f.filename}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {aiDraft.reasoning && (
                                        <div className="text-[11px] text-[#78736E] mt-2 italic">
                                            AI's begrundelse: {aiDraft.reasoning}
                                        </div>
                                    )}
                                </div>
                            )}
                            {chatAttachments.length > 0 && (
                                <div className="flex flex-wrap gap-2" data-testid="agent-chat-attachment-previews">
                                    {chatAttachments.map((a) => {
                                        const isImage = (a.content_type || "").startsWith("image/") || ["jpg","jpeg","png","heic","heif","webp"].includes(a.ext);
                                        return (
                                            <div
                                                key={a.id}
                                                className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E1DA] bg-[#F4F2EF] group"
                                                data-testid={`agent-chat-attachment-preview-${a.id}`}
                                            >
                                                {isImage ? (
                                                    <img src={a.localUrl} alt={a.filename} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center p-1 text-center">
                                                        <FileText className="w-5 h-5 text-[#78736E]" />
                                                        <span className="text-[9px] text-[#78736E] truncate w-full mt-1">{a.filename}</span>
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => removeChatAttachment(a.id)}
                                                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#2C2A29]/70 text-white flex items-center justify-center hover:bg-[#2C2A29]"
                                                    data-testid={`remove-agent-chat-attachment-${a.id}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <Textarea
                                rows={3}
                                placeholder="Skriv en besked til kunden..."
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                disabled={sendingMessage}
                                data-testid="customer-message-input"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendMessage();
                                }}
                            />
                            <input
                                ref={chatAttachRef}
                                type="file"
                                accept="image/*,.heic,.heif,application/pdf"
                                multiple
                                className="hidden"
                                data-testid="agent-chat-attach-input"
                                onChange={(e) => { uploadChatAttachments(e.target.files); e.target.value = ""; }}
                            />
                            <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => chatAttachRef.current?.click()}
                                    disabled={sendingMessage || chatUploading}
                                    data-testid="agent-chat-attach-button"
                                    className="border-[#D2C9B9] hover:bg-[#F0EBE1] h-8"
                                >
                                    {chatUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                                    <span className="ml-1.5 text-xs">Vedhæft billede</span>
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={generateAIDraft}
                                    disabled={sendingMessage || aiDrafting}
                                    data-testid="ai-suggest-button"
                                    className="border-[#4A4641] text-[#4A4641] hover:bg-[#F0EBE1] h-8"
                                >
                                    {aiDrafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                    <span className="ml-1.5 text-xs">{aiDrafting ? "Genererer..." : "Foreslå svar med AI"}</span>
                                </Button>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-[#5C5854] cursor-pointer" data-testid="include-booking-toggle">
                                <Checkbox
                                    checked={includeBooking}
                                    onCheckedChange={(v) => { setIncludeBooking(!!v); if (v) { setIncludeBooking4w(false); setIncludeBooking6w(false); } }}
                                    data-testid="include-booking-checkbox"
                                />
                                <Calendar className="w-3.5 h-3.5 text-[#993C3C]" />
                                <span>Tilføj <strong>"Book en tid hurtigst muligt"</strong>-knap til kunden</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm text-[#5C5854] cursor-pointer" data-testid="include-booking-4w-toggle">
                                <Checkbox
                                    checked={includeBooking4w}
                                    onCheckedChange={(v) => { setIncludeBooking4w(!!v); if (v) { setIncludeBooking(false); setIncludeBooking6w(false); } }}
                                    data-testid="include-booking-4w-checkbox"
                                />
                                <Calendar className="w-3.5 h-3.5 text-[#4A4641]" />
                                <span>Tilføj <strong>"Book en tid om 4 uger"</strong>-knap til kunden</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm text-[#5C5854] cursor-pointer" data-testid="include-booking-6w-toggle">
                                <Checkbox
                                    checked={includeBooking6w}
                                    onCheckedChange={(v) => { setIncludeBooking6w(!!v); if (v) { setIncludeBooking(false); setIncludeBooking4w(false); } }}
                                    data-testid="include-booking-6w-checkbox"
                                />
                                <Calendar className="w-3.5 h-3.5 text-[#4A4641]" />
                                <span>Tilføj <strong>"Book en tid om 6 uger"</strong>-knap til kunden</span>
                            </label>
                            <div className="flex items-center justify-between">
                                <div className="text-xs text-[#78736E]">Kunden får mail-notifikation</div>
                                <Button
                                    onClick={sendMessage}
                                    disabled={(!newMessage.trim() && chatAttachments.length === 0) || sendingMessage}
                                    className="bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                                    data-testid="send-customer-message-button"
                                >
                                    {sendingMessage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                    Send til kunde
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Internal correspondence (employees / suppliers) */}
                    <InternalCorrespondenceCard complaint={complaint} onChanged={load} />

                    {/* Notes */}
                    <Card title="Interne noter">
                        <div className="space-y-3 mb-4">
                            {complaint.notes?.length === 0 && <div className="text-sm text-[#78736E]">Ingen noter endnu</div>}
                            {complaint.notes?.map((n) => (
                                <div key={n.id} className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-3" data-testid={`note-${n.id}`}>
                                    <div className="flex items-center justify-between text-xs text-[#92400E] mb-1">
                                        <span className="font-semibold">{n.author_name}</span>
                                        <span>{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
                                    </div>
                                    <div className="text-sm text-[#78350F] whitespace-pre-wrap">{n.text}</div>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-2">
                            <Textarea
                                rows={3}
                                placeholder="Tilføj en intern note..."
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                data-testid="new-note-input"
                            />
                            <Button onClick={addNote} disabled={!newNote.trim()} className="self-start bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="add-note-button">
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Tilføj note
                            </Button>
                        </div>
                    </Card>
                </div>

                {/* Right: actions */}
                <div className="space-y-6">
                    <Card title="Handlinger">
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Status</Label>
                                <div className="mt-1.5" data-testid="status-static">
                                    <StatusBadge status={complaint.status} />
                                </div>
                                <div className="text-xs text-[#78736E] mt-2">Status opdateres automatisk når du videresender sagen.</div>
                            </div>
                            <Separator className="bg-[#E5E1DA]" />
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Ansvarlig</Label>
                                <div className="mt-1.5 text-sm text-[#2C2A29] bg-[#F4F2EF] border border-[#E5E1DA] rounded-md px-3 py-2" data-testid="assigned-static">
                                    {complaint.supplier_id
                                        ? `Leverandør: ${complaint.supplier_name || ""}`
                                        : complaint.assigned_to_name
                                            ? `Medarbejder: ${complaint.assigned_to_name}`
                                            : "Ikke tildelt"}
                                </div>
                                {complaint.escalated_to_leader_name && (
                                    <div className="mt-2 flex items-center gap-2 text-xs text-[#5B4B8A] bg-[#EEF2FF] border border-[#C7D2FE] rounded-md px-3 py-2" data-testid="escalation-static">
                                        <span className="font-semibold">Eskaleret til leder:</span>
                                        <span>{complaint.escalated_to_leader_name}</span>
                                    </div>
                                )}
                            </div>
                            {!isMedarbejder && complaint.follow_up?.active && !complaint.follow_up?.contacted_confirmed && (
                                <EscalateLeaderCard complaint={complaint} onChanged={load} />
                            )}
                        </div>
                    </Card>

                    {/* Activity */}
                    <Card title="Historik">
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {[...(complaint.activity || [])].reverse().map((a) => (
                                <div key={a.id} className="flex gap-3 text-sm" data-testid={`activity-${a.id}`}>
                                    <div className="w-8 h-8 rounded-full bg-[#F0EBE1] flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Activity className="w-3.5 h-3.5 text-[#4A4641]" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-[#2C2A29] font-medium">{a.details}</div>
                                        <div className="text-xs text-[#78736E] mt-0.5">
                                            {a.actor_name} · {(a.timestamp || "").slice(0, 16).replace("T", " ")}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white border border-[#E5E1DA] rounded-xl p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-4">{title}</div>
            {children}
        </div>
    );
}

function Info({ k, v, colSpan }) {
    return (
        <div className={colSpan ? "sm:col-span-2" : ""}>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-0.5">{k}</div>
            <div className="text-sm text-[#2C2A29]">{v}</div>
        </div>
    );
}

function CRMPanel({ complaint, onRefreshed }) {
    const crm = complaint.crm_data;
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        setRefreshing(true);
        try {
            await api.post(`/complaints/${complaint.id}/crm-lookup`);
            // Reload the full complaint so order_number auto-fill and other side-effects are picked up
            const { data } = await api.get(`/complaints/${complaint.id}`);
            onRefreshed(data);
            toast.success(data.crm_data?.found ? "CRM-data opdateret" : "Fandt intet i CRM");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke hente CRM-data");
        } finally {
            setRefreshing(false);
        }
    };

    const fmtChecked = crm?.checked_at
        ? new Intl.DateTimeFormat("da-DK", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(crm.checked_at))
        : null;

    return (
        <div className="mt-5 pt-4 border-t border-[#F0EBE1]" data-testid="crm-panel">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#78736E]">
                    <Database className="w-3.5 h-3.5" />
                    CRM-opslag (AZZET.DK)
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={refreshing}
                    className="text-xs text-[#4A4641] hover:text-[#2C2A29] flex items-center gap-1 disabled:opacity-50"
                    data-testid="crm-refresh-button"
                >
                    {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Genopfrisk
                </button>
            </div>
            {!crm && (
                <div className="text-xs text-[#78736E] italic">
                    Endnu ikke slået op — klik Genopfrisk for at hente data fra CRM.
                </div>
            )}
            {crm && !crm.found && (
                <div className="text-xs text-[#78736E]" data-testid="crm-no-match">
                    Ingen matchende tilbud fundet i CRM
                    {crm.reason ? ` (${crm.reason})` : ""}
                    {crm.error ? ` · Fejl: ${crm.error}` : ""}.
                    {fmtChecked && <div className="mt-0.5 text-[10px]">Sidst tjekket {fmtChecked}</div>}
                </div>
            )}
            {crm && crm.found && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="crm-match">
                    {crm.our_reference && (
                        <div className="sm:col-span-2 bg-[#F0EBE1] rounded-md px-3 py-2 border border-[#D2C9B9]">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Our reference (til leverandør)</div>
                            <div className="text-sm text-[#2C2A29] font-mono" data-testid="crm-our-reference">{crm.our_reference}</div>
                        </div>
                    )}
                    <Info k="Tilbudsnummer" v={crm.quote_id || "-"} />
                    <Info k="Ordrenummer i CRM" v={crm.order_id || "-"} />
                    <Info k="Konsulent" v={crm.consultant_name || "-"} />
                    {crm.quote_pdf_url && (
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-0.5">Tilbud PDF</div>
                            <a
                                href={crm.quote_pdf_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-[#4A4641] hover:text-[#2C2A29] underline inline-flex items-center gap-1"
                                data-testid="crm-pdf-link"
                            >
                                Åbn tilbud
                                <ExternalLink className="w-3 h-3" />
                            </a>
                        </div>
                    )}
                    {fmtChecked && (
                        <div className="sm:col-span-2 text-[10px] text-[#78736E]">Sidst hentet {fmtChecked}</div>
                    )}
                </div>
            )}
        </div>
    );
}

function EditableField({ label, value, editable, inputType = "text", testidPrefix, onSave, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(value || "");
    const [saving, setSaving] = useState(false);

    const startEdit = () => {
        setText(value || "");
        setEditing(true);
    };

    const cancel = () => {
        setEditing(false);
        setText(value || "");
    };

    const save = async () => {
        const trimmed = text.trim();
        if (trimmed === (value || "").trim()) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            const fresh = await onSave(trimmed);
            onUpdated(fresh);
            toast.success(`${label} opdateret`);
            setEditing(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || `Kunne ikke opdatere ${label.toLowerCase()}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-0.5">{label}</div>
            {editing ? (
                <div className="flex items-center gap-1.5">
                    <Input
                        type={inputType}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") save();
                            if (e.key === "Escape") cancel();
                        }}
                        disabled={saving}
                        maxLength={200}
                        autoFocus
                        className="h-8 text-sm"
                        data-testid={`${testidPrefix}-input`}
                    />
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="h-8 w-8 rounded-md bg-[#4A4641] hover:bg-[#2C2A29] text-white flex items-center justify-center shrink-0 disabled:opacity-50"
                        data-testid={`${testidPrefix}-save`}
                        title="Gem"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                    <button
                        type="button"
                        onClick={cancel}
                        disabled={saving}
                        className="h-8 w-8 rounded-md border border-[#E5E1DA] hover:bg-[#F4F2EF] text-[#78736E] flex items-center justify-center shrink-0"
                        data-testid={`${testidPrefix}-cancel`}
                        title="Annullér"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-2 group">
                    <span className="text-sm text-[#2C2A29] break-all" data-testid={`${testidPrefix}-value`}>{value || "-"}</span>
                    {editable && (
                        <button
                            type="button"
                            onClick={startEdit}
                            className="opacity-40 group-hover:opacity-100 transition-opacity text-[#78736E] hover:text-[#2C2A29] shrink-0"
                            data-testid={`${testidPrefix}-edit`}
                            title={`Rediger ${label.toLowerCase()}`}
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function ForwardDialog({ complaint, suppliers, users, leaders = [], onSent, restrictToSuppliers = false }) {
    const [open, setOpen] = useState(false);
    const [recipientType, setRecipientType] = useState("supplier");
    const [supplierId, setSupplierId] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [leaderId, setLeaderId] = useState("");
    const [customEmail, setCustomEmail] = useState("");
    const [recipientName, setRecipientName] = useState("");
    const [message, setMessage] = useState("");
    const [includeNotes, setIncludeNotes] = useState(false);
    const [selectedFileIds, setSelectedFileIds] = useState(new Set());
    const [sending, setSending] = useState(false);

    // Build the list of attachable files (case files + receipt) with metadata
    const allFileIds = [
        ...((complaint.file_ids) || []),
        ...(complaint.receipt_file_id ? [complaint.receipt_file_id] : []),
    ];
    const attachableFiles = allFileIds.map((fid) => {
        const meta = (complaint.files || []).find((f) => f.id === fid) || {};
        const isReceipt = fid === complaint.receipt_file_id;
        return {
            id: fid,
            name: meta.original_filename || meta.filename || "fil",
            content_type: meta.content_type || "",
            is_receipt: isReceipt,
        };
    });

    // Default select all when dialog opens
    useEffect(() => {
        if (open) {
            setSelectedFileIds(new Set(allFileIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, complaint.id]);

    const toggleFile = (fid) => {
        setSelectedFileIds((prev) => {
            const next = new Set(prev);
            if (next.has(fid)) next.delete(fid); else next.add(fid);
            return next;
        });
    };
    const toggleAll = () => {
        setSelectedFileIds((prev) =>
            prev.size === attachableFiles.length ? new Set() : new Set(attachableFiles.map((f) => f.id))
        );
    };

    const send = async () => {
        setSending(true);
        try {
            const payload = {
                recipient_type: recipientType,
                supplier_id: recipientType === "supplier" ? supplierId : null,
                employee_id: recipientType === "employee" ? employeeId : null,
                leader_id: recipientType === "leader" ? leaderId : null,
                custom_email: recipientType === "custom" ? customEmail : null,
                recipient_name: recipientName,
                message,
                include_internal_notes: includeNotes,
                file_ids: Array.from(selectedFileIds),
            };
            const { data } = await api.post(`/complaints/${complaint.id}/forward`, payload);
            if (data.mocked) {
                toast.info("Email mocked (ingen Resend nøgle sat). Modtager: " + data.recipient);
            } else {
                toast.success(`Sendt til ${data.recipient}`);
            }
            setOpen(false);
            onSent();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke videresende");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="forward-button">
                    <Send className="w-4 h-4 mr-2" />
                    Videresend sag
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading">Videresend sag {complaint.case_number}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Modtager</Label>
                        <Select value={recipientType} onValueChange={setRecipientType}>
                            <SelectTrigger className="mt-1.5" data-testid="forward-recipient-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="supplier">Leverandør</SelectItem>
                                {!restrictToSuppliers && <SelectItem value="employee">Medarbejder</SelectItem>}
                                {!restrictToSuppliers && <SelectItem value="leader">Leder</SelectItem>}
                                <SelectItem value="custom">Anden email</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {recipientType === "supplier" && (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Vælg leverandør</Label>
                            <Select value={supplierId} onValueChange={setSupplierId}>
                                <SelectTrigger className="mt-1.5" data-testid="forward-supplier-select">
                                    <SelectValue placeholder="Vælg" />
                                </SelectTrigger>
                                <SelectContent>
                                    {suppliers.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {recipientType === "employee" && (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Vælg medarbejder</Label>
                            <Select value={employeeId} onValueChange={setEmployeeId}>
                                <SelectTrigger className="mt-1.5" data-testid="forward-employee-select">
                                    <SelectValue placeholder="Vælg" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.filter((u) => u.role === "medarbejder").map((u) => (
                                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="text-xs text-[#78736E] mt-2">Kunden får automatisk besked om at sagen er sendt videre. Medarbejderen får påmindelser indtil de bekræfter kontakt med kunden.</div>
                        </div>
                    )}
                    {recipientType === "leader" && (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Vælg leder</Label>
                            <Select value={leaderId} onValueChange={setLeaderId}>
                                <SelectTrigger className="mt-1.5" data-testid="forward-leader-select">
                                    <SelectValue placeholder="Vælg" />
                                </SelectTrigger>
                                <SelectContent>
                                    {leaders.map((l) => (
                                        <SelectItem key={l.id} value={l.id}>{l.name} ({l.email})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {recipientType === "custom" && (
                        <>
                            <div>
                                <Label>Modtagers navn</Label>
                                <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} data-testid="forward-custom-name" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)} data-testid="forward-custom-email" />
                            </div>
                        </>
                    )}
                    <div>
                        <Label>Besked (valgfri)</Label>
                        <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tilføj en kort besked til modtageren..." data-testid="forward-message" />
                    </div>
                    <div className="space-y-3 pt-2">
                        {attachableFiles.length > 0 ? (
                            <div data-testid="forward-attachments">
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">
                                        Vedhæftninger ({selectedFileIds.size}/{attachableFiles.length})
                                    </Label>
                                    <button
                                        type="button"
                                        onClick={toggleAll}
                                        className="text-xs text-[#78736E] hover:text-[#2C2A29] underline"
                                        data-testid="forward-toggle-all-files"
                                    >
                                        {selectedFileIds.size === attachableFiles.length ? "Fravælg alle" : "Vælg alle"}
                                    </button>
                                </div>
                                <div className="max-h-72 overflow-y-auto border border-[#E5E1DA] rounded-lg p-2 bg-[#FAF9F7]">
                                    <div className="grid grid-cols-3 gap-2">
                                        {attachableFiles.map((f) => {
                                            const ctype = f.content_type || "";
                                            const isImage = ctype.startsWith("image/");
                                            const isVideo = ctype.startsWith("video/");
                                            const checked = selectedFileIds.has(f.id);
                                            const tok = getToken();
                                            const thumbUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download?auth=${tok}&size=thumb`;
                                            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download?auth=${tok}`;
                                            return (
                                                <label
                                                    key={f.id}
                                                    className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer group ${checked ? "border-[#4A4641] shadow-sm" : "border-transparent opacity-50 hover:opacity-80"}`}
                                                    data-testid={`forward-file-tile-${f.id}`}
                                                    title={f.name}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleFile(f.id)}
                                                        className="sr-only"
                                                        data-testid={`forward-file-${f.id}`}
                                                    />
                                                    {isImage ? (
                                                        <img
                                                            src={thumbUrl}
                                                            alt={f.name}
                                                            loading="lazy"
                                                            decoding="async"
                                                            className="w-full h-full object-cover bg-[#E5E1DA]"
                                                        />
                                                    ) : isVideo ? (
                                                        <video
                                                            src={fullUrl}
                                                            preload="metadata"
                                                            muted
                                                            className="w-full h-full object-cover bg-[#2C2A29]"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-[#E5E1DA] text-[#78736E]">
                                                            <FileText className="w-6 h-6" />
                                                        </div>
                                                    )}
                                                    {/* Checkmark indicator */}
                                                    <div className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${checked ? "bg-[#4A4641] text-white" : "bg-white/80 text-[#78736E] border border-[#D4CFC5]"}`}>
                                                        {checked ? "✓" : ""}
                                                    </div>
                                                    {/* Type badge */}
                                                    {(isVideo || f.is_receipt) && (
                                                        <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider">
                                                            {f.is_receipt ? "Kvittering" : "Video"}
                                                        </div>
                                                    )}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                                <p className="text-xs text-[#A19E9A] mt-1.5">Klik på et billede for at fra- eller tilvælge det.</p>
                            </div>
                        ) : (
                            <div className="text-xs text-[#A19E9A]">Ingen vedhæftninger på sagen.</div>
                        )}
                        <label className="flex items-center gap-2 text-sm">
                            <Checkbox checked={includeNotes} onCheckedChange={(v) => setIncludeNotes(!!v)} data-testid="forward-include-notes" />
                            Inkludér interne noter
                        </label>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Annuller</Button>
                    <Button onClick={send} disabled={sending} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="forward-send-button">
                        {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Send
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function StatusNotifyDialog({ complaint, onSend }) {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(complaint.status);
    const [note, setNote] = useState("");

    const submit = () => {
        onSend(status, true, note);
        setOpen(false);
        setNote("");
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="mt-2 w-full border-[#E5E1DA]" data-testid="notify-customer-button">
                    <UserCheck className="w-4 h-4 mr-2" />
                    Opdater status + informér kunde
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading">Informér kunde om statusændring</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Ny status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="mt-1.5">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Besked (valgfri)</Label>
                        <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Valgfri personlig besked..." data-testid="notify-customer-note" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Annuller</Button>
                    <Button onClick={submit} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="notify-customer-send">Send</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function InternalCorrespondenceCard({ complaint, onChanged }) {
    const [text, setText] = useState("");
    const [recipientId, setRecipientId] = useState("");
    const [sending, setSending] = useState(false);
    const recipients = complaint.forward_recipients || [];
    const messages = complaint.internal_messages || [];

    // Auto-select most recent recipient if not chosen
    useEffect(() => {
        if (!recipientId && recipients.length > 0) {
            setRecipientId(recipients[recipients.length - 1].id);
        }
    }, [recipients, recipientId]);

    const send = async () => {
        const trimmed = text.trim();
        if (!trimmed) return;
        setSending(true);
        try {
            await api.post(`/complaints/${complaint.id}/internal-messages`, {
                text: trimmed,
                recipient_id: recipientId || null,
            });
            setText("");
            await onChanged();
            toast.success(recipientId ? "Beskeden er sendt" : "Beskeden er gemt i tråden");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke sende beskeden");
        } finally {
            setSending(false);
        }
    };

    const copyLink = (recipient) => {
        const url = `${window.location.origin}/intern/${recipient.access_token}`;
        navigator.clipboard?.writeText(url);
        toast.success(`Link til ${recipient.name} kopieret`);
    };

    return (
        <Card title="Intern korrespondance">
            <div className="text-xs text-[#78736E] -mt-2 mb-3">
                Privat tråd med leverandører og medarbejdere — <strong>ikke synlig for kunden</strong>.
            </div>

            {/* Recipients list with copy link */}
            {recipients.length > 0 && (
                <div className="mb-4 space-y-2" data-testid="forward-recipients-list">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Modtagere med adgang</div>
                    {recipients.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#F4F2EF] border border-[#E5E1DA]" data-testid={`recipient-${r.id}`}>
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-[#2C2A29] truncate">{r.name}</div>
                                <div className="text-xs text-[#78736E] truncate">{r.role_label} · {r.email}</div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyLink(r)}
                                className="text-xs h-7 text-[#78736E] hover:bg-white"
                                data-testid={`copy-recipient-link-${r.id}`}
                            >
                                Kopiér link
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {/* Thread */}
            <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg p-4 mb-4 max-h-96 overflow-y-auto" data-testid="internal-thread">
                {messages.length === 0 && (
                    <div className="text-sm text-[#78736E] text-center py-8">
                        Ingen interne beskeder endnu — videresend sagen for at starte en tråd, eller skriv en besked nedenfor.
                    </div>
                )}
                {messages.map((m) => {
                    const isAgent = m.sender_type === "agent";
                    const bubbleColor = isAgent ? agentColor(m.sender_id || m.sender_name) : null;
                    return (
                        <div
                            key={m.id}
                            className={`flex mb-3 ${isAgent ? "justify-end" : "justify-start"}`}
                            data-testid={`internal-msg-${m.id}`}
                        >
                            <div
                                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                                    isAgent
                                        ? "text-white rounded-br-sm"
                                        : "bg-white border border-[#E5E1DA] text-[#2C2A29] rounded-bl-sm"
                                }`}
                                style={bubbleColor ? { backgroundColor: bubbleColor } : undefined}
                            >
                                <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isAgent ? "text-white/60" : "text-[#78736E]"}`}>
                                    {isAgent
                                        ? `${firstName(m.sender_name)} (${roleLabel(m.sender_role)})${m.recipient_name ? ` → ${firstName(m.recipient_name)}` : ""}`
                                        : `${firstName(m.sender_name)} · ${m.sender_role_label || "modtager"}`}
                                </div>
                                <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
                                <div className={`text-[10px] mt-1 ${isAgent ? "text-white/50" : "text-[#78736E]"}`}>
                                    {(m.created_at || "").slice(0, 16).replace("T", " ")}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Reply form */}
            {recipients.length === 0 ? (
                <div className="text-sm text-[#78736E] bg-[#F4F2EF] rounded-lg p-3 border border-[#E5E1DA]">
                    Videresend sagen til en medarbejder eller leverandør for at starte en intern tråd.
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Send til</Label>
                        <Select value={recipientId} onValueChange={setRecipientId}>
                            <SelectTrigger className="mt-1.5" data-testid="internal-recipient-select">
                                <SelectValue placeholder="Vælg modtager" />
                            </SelectTrigger>
                            <SelectContent>
                                {recipients.map((r) => (
                                    <SelectItem key={r.id} value={r.id}>
                                        {r.name} · {r.role_label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Textarea
                        rows={3}
                        placeholder="Skriv en besked til modtageren..."
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        disabled={sending}
                        data-testid="internal-message-text"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                        }}
                    />
                    <div className="flex items-center justify-between">
                        <div className="text-xs text-[#78736E]">Modtageren får mail med link til portalen</div>
                        <Button
                            onClick={send}
                            disabled={!text.trim() || sending || !recipientId}
                            className="bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                            data-testid="send-internal-message-button"
                        >
                            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                            Send
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function EscalateLeaderCard({ complaint, onChanged }) {
    const [leaders, setLeaders] = useState([]);
    const [leaderId, setLeaderId] = useState("");
    const [escalating, setEscalating] = useState(false);
    const fu = complaint.follow_up || {};
    const startedAt = fu.started_at ? new Date(fu.started_at) : null;
    const daysPassed = startedAt ? Math.floor((Date.now() - startedAt.getTime()) / (1000 * 60 * 60 * 24)) : 0;
    const canEscalate = daysPassed >= 4 && !fu.contacted_confirmed;

    useEffect(() => {
        let cancelled = false;
        api.get("/leaders").then(({ data }) => { if (!cancelled) setLeaders(data); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const escalate = async () => {
        if (!leaderId) {
            toast.error("Vælg en leder");
            return;
        }
        setEscalating(true);
        try {
            await api.post(`/complaints/${complaint.id}/escalate-leader`, { leader_id: leaderId });
            toast.success("Sag eskaleret til leder");
            await onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke eskalere");
        } finally {
            setEscalating(false);
        }
    };

    if (!canEscalate) {
        return (
            <div className="bg-[#F4F2EF] border border-[#E5E1DA] rounded-md px-3 py-2 text-xs text-[#78736E]" data-testid="follow-up-status">
                Opfølgning aktiv · Dag {daysPassed} af 4 · Eskalering bliver muligt på dag 4.
            </div>
        );
    }

    return (
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-3 space-y-2" data-testid="escalate-leader-card">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#92400E]">Eskaler til leder</div>
            <div className="text-xs text-[#92400E]/80">Medarbejder har ikke bekræftet kontakt med kunden efter {daysPassed} dage.</div>
            <Select value={leaderId} onValueChange={setLeaderId}>
                <SelectTrigger data-testid="escalate-leader-select">
                    <SelectValue placeholder="Vælg leder" />
                </SelectTrigger>
                <SelectContent>
                    {leaders.length === 0 && <div className="px-2 py-1 text-xs text-[#78736E]">Ingen ledere oprettet</div>}
                    {leaders.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name} ({l.email})</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Button
                onClick={escalate}
                disabled={escalating || !leaderId}
                size="sm"
                className="w-full bg-[#92400E] hover:bg-[#78350F] text-white"
                data-testid="escalate-leader-button"
            >
                {escalating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Send til leder
            </Button>
        </div>
    );
}

